import React, { useEffect, useRef, useState } from 'react';
import { Terminal as XTerm } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import '@xterm/xterm/css/xterm.css';
import io, { Socket } from 'socket.io-client';

interface TerminalProps {
  mode: 'local' | 'ssh' | 'wsl';
  workingDirectory?: string;
  sshConfig?: {
    host: string;
    port: number;
    username: string;
    password?: string;
    privateKey?: string;
  };
}

const Terminal: React.FC<TerminalProps> = ({ mode, workingDirectory, sshConfig }) => {
  const terminalRef = useRef<HTMLDivElement>(null);
  const [terminal, setTerminal] = useState<XTerm | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!terminalRef.current) return;

    // Clear any existing terminal content
    terminalRef.current.innerHTML = '';

    const term = new XTerm({
      theme: {
        background: '#0C0C0C',
        foreground: '#33FF33',
        cursor: '#33FF33',
        cursorAccent: '#0C0C0C',
        selectionBackground: 'rgba(51, 255, 51, 0.3)',
        black: '#000000',
        red: '#FF3333',
        green: '#33FF33',
        yellow: '#FFFF33',
        blue: '#3366FF',
        magenta: '#FF33FF',
        cyan: '#33FFFF',
        white: '#FFFFFF',
        brightBlack: '#666666',
        brightRed: '#FF6666',
        brightGreen: '#66FF66',
        brightYellow: '#FFFF66',
        brightBlue: '#6699FF',
        brightMagenta: '#FF66FF',
        brightCyan: '#66FFFF',
        brightWhite: '#FFFFFF'
      },
      fontFamily: 'JetBrains Mono, Consolas, Monaco, monospace',
      fontSize: 14,
      cursorBlink: true,
      cursorStyle: 'block',
      scrollback: 10000,
      convertEol: true,
      scrollOnUserInput: true,
      rightClickSelectsWord: true,
      allowProposedApi: true
    });

    const fitAddon = new FitAddon();
    const webLinksAddon = new WebLinksAddon();

    term.loadAddon(fitAddon);
    term.loadAddon(webLinksAddon);
    term.open(terminalRef.current);
    
    // Wait for the terminal to be properly mounted before fitting
    setTimeout(() => {
      if (term.element && term.element.offsetParent) {
        fitAddon.fit();
      }
    }, 100);

    const newSocket = io('/terminal', { 
      path: '/terminal/socket.io/',
      transports: ['websocket', 'polling']
    });
    
    newSocket.on('connect', () => {
      term.writeln('\r\n🚀 Welcome to Omega AI Terminal\r\n');
      term.writeln('Powered by Omega Systems Technology\r\n');
      
      // Authenticate first
      newSocket.emit('auth', { sessionId: 'omega-ai-session' });
    });
    
    newSocket.on('authenticated', () => {
      setIsConnected(true);
      
      if (mode === 'local' || mode === 'wsl') {
        newSocket.emit('create-local-terminal', { 
          workingDirectory, 
          useWSL: mode === 'wsl' 
        });
      } else if (mode === 'ssh' && sshConfig) {
        term.writeln(`Connecting to ${sshConfig.host}...\r\n`);
        newSocket.emit('ssh-connect', {
          host: sshConfig.host,
          username: sshConfig.username,
          password: sshConfig.password
        });
      }
    });

    newSocket.on('connected', (data) => {
      term.writeln('✓ Terminal session established\r\n');
      if (mode === 'local' || mode === 'wsl') {
        term.writeln('Ready to start Omega AI assistant\r\n');
      }
    });

    newSocket.on('data', (data: string) => {
      term.write(data);
    });

    newSocket.on('error', (data: { message: string }) => {
      term.writeln(`\r\n❌ Error: ${data.message}\r\n`);
    });

    newSocket.on('exit', () => {
      term.writeln('\r\n\r\n[Terminal session ended]');
      setIsConnected(false);
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
      term.writeln('\r\n\r\n[Disconnected from server]');
    });

    term.onData((data) => {
      if (isConnected) {
        newSocket.emit('data', data);
      }
    });

    term.onResize(({ cols, rows }) => {
      if (isConnected) {
        newSocket.emit('resize', { cols, rows });
      }
    });

    const handleResize = () => {
      if (term.element && term.element.offsetParent) {
        fitAddon.fit();
      }
    };

    window.addEventListener('resize', handleResize);

    setTerminal(term);
    setSocket(newSocket);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (newSocket.connected) {
        newSocket.disconnect();
      }
      if (term) {
        term.dispose();
      }
    };
  }, [mode, workingDirectory, sshConfig]);

  return (
    <div className="relative w-full h-full bg-terminal-bg rounded-lg overflow-hidden shadow-2xl border border-omega-accent">
      <div className="absolute top-0 left-0 right-0 bg-omega-darker p-2 flex items-center justify-between border-b border-omega-accent">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <div className="text-omega-accent font-mono text-sm">
          Omega AI Terminal - {mode === 'local' ? 'Local' : mode === 'wsl' ? 'Ubuntu (WSL)' : `SSH: ${sshConfig?.host}`}
        </div>
        <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
      </div>
      <div ref={terminalRef} className="w-full h-full pt-10" />
    </div>
  );
};

export default Terminal;